import * as React from 'react';
import { useState, useEffect } from 'react';
import { sortOrderType } from 'ui/sort-order/constants';
import { DefaultSortOrder, DomainNode, SortOptions } from 'ui/sort-order/types';
import { getNotSelectedOptions, sortDefaultSortOrder } from 'ui/sort-order/utils';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { SortByList } from 'ui/sort-order/sort-by-list/sort-by-list';

interface SortOrderModalProps {
  options?: SortOptions;
  translation?: any;
  onDone?: (options: SortOptions) => any;
  handleToggle: () => void;
  loadOptions: () => void;
}

// eslint:disable-next-line: max-func-body-length
const SortOrderModal = ({ options, translation, onDone: onDoneCb, handleToggle: handleToggleCb, loadOptions }: SortOrderModalProps) => {
  const [optionsState, setOptionsState] = useState(options);
  useEffect(() => {
    loadOptions();
  }, []);

  useEffect(() => {
    setOptionsState(options);
  }, [options]);

  const handleAddSortArgument = (e: React.SyntheticEvent<any>) => {
    e.preventDefault();
    const newOptions = { ...optionsState };
    const defaultSortOrder = [...optionsState.defaultSortOrder];

    const orderBySequence = defaultSortOrder.map((curr) => {
      return curr.orderBySequence;
    });

    const maxOrderBySequence = (orderBySequence && orderBySequence.length) > 0 ? Math.max(...orderBySequence) : 0;
    const newSortArgument = {
      domainNode: {
        value: -1,
        description: ''
      },
      sortOrderType: sortOrderType.DESC,
      orderBySequence: maxOrderBySequence + 1
    };

    newOptions.defaultSortOrder = defaultSortOrder.concat(newSortArgument);

    setOptionsState(newOptions);
  };

  const handleDefaultOrderRemove = (index: number) => {
    const newOptions = { ...optionsState };
    const defaultSortOrder = [...optionsState.defaultSortOrder];

    defaultSortOrder.splice(index, 1);

    newOptions.defaultSortOrder = defaultSortOrder.filter((element) => {
      return element.orderBySequence < newOptions.defaultSortOrder[index].orderBySequence;
    }).concat(
      defaultSortOrder.filter((element) => {
        return element.orderBySequence > newOptions.defaultSortOrder[index].orderBySequence;
      }).map((curr) => {
        return Object.assign({}, curr, {
          orderBySequence: curr.orderBySequence - 1
        });
      }));

    setOptionsState(newOptions);
  };

  const handleDefaultOrderChange = (sortByItem: DefaultSortOrder) => {
    const newOptions = { ...optionsState };
    const defaultSortOrder = [...optionsState.defaultSortOrder];

    newOptions.defaultSortOrder = defaultSortOrder
      .filter((element) => element.orderBySequence !== sortByItem.orderBySequence)
      .concat(sortByItem);

    setOptionsState(newOptions);
  };

  const handleNotSelectedOptions = (sortByItem: DefaultSortOrder): DomainNode[] => {
    return getNotSelectedOptions(
      sortByItem,
      optionsState.defaultSortOrder,
      optionsState.sortOptions
    );
  };

  const handleMove = (domainValue: number, type: number) => {
    const newOptions = { ...optionsState };
    newOptions.defaultSortOrder = sortDefaultSortOrder(domainValue, newOptions.defaultSortOrder, type);

    setOptionsState(newOptions);
  };

  const isDisabled = () => {
    return optionsState.defaultSortOrder.filter((element) => element.domainNode.value === -1).length > 0 ? true : false;
  };

  const onDone = () => {
    onDoneCb(optionsState);
  };

  const handleToggle = () => {
    handleToggleCb();
  };

  return !!optionsState && !!optionsState.sortOptions &&
    <ModalWrapper
      headerTitle={translation.sort_order_title}
      btnTitle={translation.sort_order_button_done}
      onClose={handleToggle}
      onDone={onDone}
      isDisabled={isDisabled}>
      {optionsState.sortOptions.length > 0 ?
        <SortByList
          options={optionsState}
          translation={translation}
          onMove={handleMove}
          onAdd={handleAddSortArgument}
          onDefaultOrderChange={handleDefaultOrderChange}
          onDefaultOrderRemove={handleDefaultOrderRemove}
          onSelectedOptions={handleNotSelectedOptions} />
        : <div>{translation.sort_order_no_sort_options}</div>}
    </ModalWrapper>;
};

export { SortOrderModal };
